import { useTranslation } from "react-i18next"

import { BrandViewsPulseChip } from "@/components/creator/brand-views-pulse-chip"
import { StreakChip } from "@/components/creator/streak-chip"
import { RankBlazon } from "@/components/rank/rank-blazon"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { RankBlazonInput } from "@/types/rank"

interface WelcomeHeaderProps {
  /** OKS-388 streak prop, passed straight through from the page. The chip
   *  hides at `current_days < 1`. */
  streak?: { current_days: number } | null
  /** OKS-389 brand-views pulse prop. The chip hides when null/absent. */
  brandViewsPulse?: { count: number } | null
  /** OKS-385 tenure prop — `{ joined_on: "YYYY-MM-DD" }` for the current
   *  brand membership. Hides the tenure line when null/absent. */
  tenure?: { joined_on: string } | null
  /** OKS-539 — current-rank blazon `{name, tier, tierCount, imageUrl}`.
   *  Rendered inline (frame-less) beside the greeting; hidden when
   *  null/absent (no membership / read failure). */
  rankBlazon?: RankBlazonInput | null
  className?: string
}

/**
 * Derive the creator's first name from the display `name` (PRD Open Q5):
 * the first whitespace-delimited token, falling back to the full name when
 * there is no separable first token. `CurrentUser` carries no `first_name`
 * field, so this stays a pure FE derivation — no backend change (TRD KD-5).
 */
function firstNameOf(name: string): string {
  const trimmed = name.trim()
  if (trimmed === "") return name
  return trimmed.split(/\s+/)[0]
}

/**
 * Format a `YYYY-MM-DD` join date into a short, locale-aware "Mon D" string
 * ("Mar 14" / "14 mars"). Anchored at local midday so the civil date can't
 * slip across a timezone boundary. Returns `null` for a missing/malformed
 * value so the caller hides the tenure line.
 */
function formatTenureDate(joinedOn: string, locale: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(joinedOn)) return null
  const date = new Date(`${joinedOn}T12:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date)
}

/**
 * OKS-385 — W8 missions-home welcome-header (UX Spec §2, frame `336:27`).
 *
 * Layout: ~48px circular avatar (initials fallback) + "Hi {firstName}" +
 * muted tenure line "On the crew since {Mon D}". Below, a wrapping chips row:
 * the accent `<StreakChip>` (OKS-388) and the neutral
 * `<BrandViewsPulseChip>` (OKS-389). Either chip may be absent; the row
 * collapses cleanly when both are.
 *
 * Identity (name + avatar) is read from the shared `currentUser` context —
 * single source of truth, brand-agnostic. The streak / pulse / tenure data
 * are brand-scoped props passed by the page so the header re-renders on a
 * crew switch.
 *
 * Reading order (UX Spec §6): greeting → tenure → chips. The flame/eye icons
 * are `aria-hidden`; chip labels carry the full text.
 */
export function WelcomeHeader({
  streak,
  brandViewsPulse,
  tenure,
  rankBlazon,
  className,
}: WelcomeHeaderProps) {
  const { t, i18n } = useTranslation()
  const { currentUser } = useCurrentContext()
  const getInitials = useInitials()

  if (!currentUser) return null

  const firstName = firstNameOf(currentUser.name)
  const tenureDate = tenure
    ? formatTenureDate(tenure.joined_on, i18n.language)
    : null
  const showStreak = (streak?.current_days ?? 0) >= 1
  const showPulse = (brandViewsPulse?.count ?? 0) >= 1

  return (
    <section
      className={cn("flex flex-col gap-3", className)}
      data-testid="welcome-header"
    >
      <div className="flex items-center gap-3">
        <Avatar className="size-12 shrink-0 rounded-full">
          <AvatarImage
            src={currentUser.avatar_url ?? undefined}
            alt=""
            data-testid="welcome-header-avatar"
          />
          <AvatarFallback className="bg-muted text-muted-foreground text-sm font-semibold">
            {getInitials(currentUser.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <h1
            className="truncate text-lg font-semibold"
            data-testid="welcome-header-greeting"
          >
            {t("missions.welcome_header.greeting", { name: firstName })}
          </h1>
          {tenureDate && (
            <p
              className="text-muted-foreground truncate text-sm"
              data-testid="welcome-header-tenure"
            >
              {t("missions.welcome_header.tenure", { date: tenureDate })}
            </p>
          )}
        </div>

        {/* OKS-539 — current-rank blazon, frame-less, pushed to the row's
            trailing edge. Not decorative: the header carries no rank text,
            so the blazon's `alt` names the rank (UX Spec §6). */}
        {rankBlazon && (
          <span
            className="ml-auto shrink-0"
            data-testid="welcome-header-blazon"
          >
            <RankBlazon input={rankBlazon} size="inline" />
          </span>
        )}
      </div>

      {(showStreak || showPulse) && (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="welcome-header-chips"
        >
          {showStreak && <StreakChip currentDays={streak!.current_days} />}
          {showPulse && <BrandViewsPulseChip count={brandViewsPulse!.count} />}
        </div>
      )}
    </section>
  )
}
